/**
 * The frame sampler (ARD-1977): an episode-scoped `requestAnimationFrame` loop
 * that measures the gap between consecutive callbacks while the user scrolls,
 * a layout transition runs, or right after a click, and reports one
 * `perf.app.frame_episode` flow per episode.
 *
 * Why rAF and nothing else: WebKit (Mac desktop, Safari) has no Long Tasks and
 * no Long Animation Frames, so the gap between rAF callbacks is the only
 * per-frame signal every engine we ship to can produce.
 *
 * What the numbers mean, precisely: every value is about MAIN-THREAD rAF
 * CALLBACK GAPS during the episode, not rendered display frames and not GPU
 * smoothness. Every interval is read from ONE clock, `performance.now()`, at
 * the episode's start, inside each callback and at close. The timestamp a
 * callback is handed is not used: Chromium hands the frame's BEGIN time, which
 * can be 100+ ms older than the moment the callback runs and older than the
 * episode itself, so mixing it with `performance.now()` leaked time from
 * before the episode into the next interval; WebKit hands the callback's own
 * `performance.now()`. Reading the clock inside the callback gives both
 * engines one meaning — when the main thread reached the frame — and, being
 * monotonic, never yields a negative interval. `worst_gap_ms` is the longest interval seen, including the
 * unfinished one at close (`tail_gap_ms`), because a stall that is still in
 * progress when the closing timer fires is the freeze the user felt, not an
 * artefact.
 *
 * `jank_pct` is the headline rate: the share of the episode spent beyond
 * `JANK_THRESHOLD_MS` (25 ms, 1.5 × a 60 Hz frame) per interval,
 * `sum(max(0, gap - 25)) / elapsed`. The shape is Total Blocking Time's. The
 * per-gap clamp is the point — fast frames earn no credit, so a 120 Hz display
 * cannot bank surplus callbacks to hide a stall — and the threshold keeps
 * healthy, 1 ms-quantised WebKit frames from paying a phantom floor (see
 * `JANK_THRESHOLD_MS`). A ratio over the raw callback count cannot do this
 * job: see `FRAME_BUDGET_60_MS` for the spread it produced across panels.
 *
 * `cadence_hz` is the refresh rate the callbacks were delivered at, estimated
 * from the gaps (`cadence.ts`: the median of the single-frame cluster a p25
 * locates, snapped to a standard rate, 0 when too few ran). It ships so the cadence stays visible after
 * normalisation has taken it out of the headline: it is how you tell a
 * webview pinned at 60 Hz from a browser at 120, and one throttled to 30 Hz
 * by Low Power Mode — where every gap is ~33 ms and `jank_pct` reads ~50 %
 * for reasons that are not the app's — from both.
 *
 * The loop body is a handful of closure numbers, one write into a buffer
 * allocated once per sampler, and one `requestAnimationFrame` call: no
 * allocation, no DOM read, no logging. Admission is the observer's job
 * (`open()` assumes it was granted); `touch()` is the self-admitting form kept
 * for callers that own their own signals.
 */
import { emitPerformanceDebugEvent } from '../debug-sink';
import { cancelTracking, finishTracking, startTracking } from '../tracking';
import { type TrackingHandle, TrackingKey } from '../types';
import { estimateCadenceHz } from './cadence';
import { publishFrameEpisode } from './episodes';
import { tryAdmitEpisode } from './gate';
import {
  FRAME_BUDGET_60_MS,
  FROZEN_FRAME_MS,
  type FrameSurface,
  type FrameTrigger,
  JANK_THRESHOLD_MS,
  LONG_FRAME_MS,
} from './surfaces';

/** A scroll episode ends this long after the last scroll signal. */
export const SCROLL_IDLE_MS = 200;

/**
 * An interaction episode (`pointer` / `key`) measures this fixed window from
 * the input; touches do not extend it.
 */
export const INTERACTION_EPISODE_MS = 2_000;

/** No episode runs longer than this; it is closed with `capped: true`. */
export const MAX_EPISODE_MS = 10_000;

/**
 * An episode with fewer completed intervals than this AND no interval over
 * `LONG_FRAME_MS` is uneventful noise (a single wheel tick, a 50 ms hover
 * transition) and is discarded. An episode with few callbacks BECAUSE the
 * main thread was blocked has a long interval and is kept: slow interactions
 * deliver fewer callbacks, and filtering on the count alone would remove
 * exactly the episodes this exists to find.
 */
export const MIN_EPISODE_FRAMES = 6;

/**
 * Intervals kept for the cadence estimate: the first this-many completed
 * intervals after the opening one. ~2 s at 60 Hz, ~1 s at 120 — far more
 * than the estimator needs, and a fixed buffer is what keeps the loop
 * allocation-free.
 */
export const CADENCE_SAMPLE_SIZE = 128;

export type FrameSampler = {
  /** A signal for this sampler's element. Opens an episode if the gate admits; refreshes an open one. */
  touch: () => void;
  /** Open an episode whose admission the caller has already obtained from the gate. */
  open: () => void;
  /** Close the open episode now and report it. No-op when idle. */
  end: () => void;
  /** Discard the open episode without reporting. No-op when idle. */
  abort: () => void;
  /** Whether an episode is currently open on this sampler. */
  isOpen: () => boolean;
};

const now = (): number => performance.now();

export function createFrameSampler(
  surface: FrameSurface,
  trigger: FrameTrigger
): FrameSampler {
  let handle: TrackingHandle | null = null;
  let startedAt = 0;
  let lastFrameAt = 0;
  let frames = 0;
  let longFrames = 0;
  let frozenFrames = 0;
  let worst = 0;
  const cadenceGaps = new Float64Array(CADENCE_SAMPLE_SIZE);
  let cadenceCount = 0;
  let overshoot = 0;
  let elapsed = 0;
  let rafId = 0;
  let idleTimer: ReturnType<typeof setTimeout> | null = null;

  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') abort();
  };

  const teardown = () => {
    if (rafId !== 0) {
      cancelAnimationFrame(rafId);
      rafId = 0;
    }
    if (idleTimer !== null) {
      clearTimeout(idleTimer);
      idleTimer = null;
    }
    document.removeEventListener('visibilitychange', onVisibilityChange);
    handle = null;
  };

  // The callback's timestamp argument is deliberately not read: see the
  // header. The first interval runs from the signal that opened the episode,
  // so work done before the first frame (a tab activated on `mousedown`) is
  // in it, and nothing from before the episode is.
  const tick = () => {
    if (handle === null) return;
    const t = now();
    const gap = t - lastFrameAt;
    lastFrameAt = t;
    frames += 1;
    elapsed += gap;
    if (gap > LONG_FRAME_MS) longFrames += 1;
    if (gap > FROZEN_FRAME_MS) frozenFrames += 1;
    if (gap > worst) worst = gap;
    if (gap > JANK_THRESHOLD_MS) overshoot += gap - JANK_THRESHOLD_MS;
    // The first interval is the one gap that is not a panel cadence (it runs
    // from the signal, not from a frame), so it is never sampled.
    if (frames > 1 && cadenceCount < CADENCE_SAMPLE_SIZE) {
      cadenceGaps[cadenceCount] = gap;
      cadenceCount += 1;
    }
    if (t - startedAt >= MAX_EPISODE_MS) {
      close(true);
      return;
    }
    rafId = requestAnimationFrame(tick);
  };

  const close = (capped: boolean) => {
    const episodeHandle = handle;
    if (episodeHandle === null) return;
    // The interval that was still running when the closing timer fired. A
    // timer scheduled 200 ms after the last scroll can run BEFORE the rAF that
    // was due 500 ms ago if the main thread stalled in between; that stall is
    // the freeze the user felt and must not be dropped on the floor. It is
    // reported separately (`tail_gap_ms`) and folded into `worst_gap_ms`, but
    // not counted as a completed interval, so `frames` and `cadence_hz` keep
    // a completed-interval meaning.
    const tailGap = Math.max(0, now() - lastFrameAt);
    teardown();

    const worstGap = Math.max(worst, tailGap);
    const eventful = worstGap > LONG_FRAME_MS;
    if (frames < MIN_EPISODE_FRAMES && !eventful) {
      cancelTracking({ handle: episodeHandle });
      return;
    }

    // The tail is real time the thread did not yield, so it pays into the
    // budget like any other interval — and into the denominator with it, which
    // is what keeps `jank_pct` inside 0 to 100.
    const totalOvershoot = overshoot + Math.max(0, tailGap - JANK_THRESHOLD_MS);
    const totalElapsed = elapsed + tailGap;
    const jankPct =
      totalElapsed > 0 ? Math.round((totalOvershoot / totalElapsed) * 1000) / 10 : 0;
    const metadata = {
      frames,
      long_frames: longFrames,
      frozen_frames: frozenFrames,
      worst_gap_ms: Math.round(worstGap),
      tail_gap_ms: Math.round(tailGap),
      jank_pct: jankPct,
      dropped_frames_60: Math.round(totalOvershoot / FRAME_BUDGET_60_MS),
      cadence_hz: estimateCadenceHz(cadenceGaps, cadenceCount),
      capped,
    };
    const result = finishTracking({ handle: episodeHandle, metadata });
    if (!result) return;

    publishFrameEpisode({
      surface,
      trigger,
      ...metadata,
      totalDurationMs: result.totalDurationMs,
      completedAt: Date.now(),
    });
    emitPerformanceDebugEvent({
      type: 'frame_episode',
      key: TrackingKey.APP_FRAME_EPISODE,
      detail: `${surface} · ${frames} frames · ${jankPct}% jank · worst ${metadata.worst_gap_ms}ms · ${metadata.cadence_hz}Hz`,
      elapsedMs: result.totalDurationMs,
    });
  };

  const armIdleTimer = () => {
    if (trigger === 'transition') return;
    // An interaction window is fixed from its input; only scroll refreshes.
    const fixedWindow = trigger !== 'scroll';
    if (fixedWindow && idleTimer !== null) return;
    if (idleTimer !== null) clearTimeout(idleTimer);
    idleTimer = setTimeout(
      () => {
        idleTimer = null;
        close(false);
      },
      fixedWindow ? INTERACTION_EPISODE_MS : SCROLL_IDLE_MS
    );
  };

  const begin = (t: number) => {
    handle = startTracking({
      key: TrackingKey.APP_FRAME_EPISODE,
      instanced: true,
      metadata: { surface, trigger },
      // Up to 120 episodes an hour for the life of a desktop document would
      // leave four user-timing entries each in a buffer nothing clears.
      userTiming: false,
    });
    startedAt = t;
    lastFrameAt = t;
    frames = 0;
    longFrames = 0;
    frozenFrames = 0;
    worst = 0;
    cadenceCount = 0;
    overshoot = 0;
    elapsed = 0;
    document.addEventListener('visibilitychange', onVisibilityChange);
    rafId = requestAnimationFrame(tick);
    armIdleTimer();
  };

  const isHidden = () =>
    typeof document !== 'undefined' && document.visibilityState === 'hidden';

  const open = () => {
    if (handle !== null) {
      armIdleTimer();
      return;
    }
    if (isHidden()) return;
    begin(now());
  };

  const touch = () => {
    if (handle !== null) {
      armIdleTimer();
      return;
    }
    if (isHidden()) return;
    const t = now();
    if (!tryAdmitEpisode(t)) return;
    begin(t);
  };

  const end = () => {
    close(false);
  };

  const abort = () => {
    const episodeHandle = handle;
    if (episodeHandle === null) return;
    teardown();
    cancelTracking({ handle: episodeHandle });
  };

  const isOpen = () => handle !== null;

  return { touch, open, end, abort, isOpen };
}
